import { useState } from "react";
import { Link } from "react-router-dom";
import { api, policySlug } from "../lib/api";
import { toast } from "sonner";
import { LinkedinLogo } from "@phosphor-icons/react";
import { useTheme } from "../lib/theme";

export default function Footer() {
  const { settings } = useTheme();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email, source: "footer" });
      toast.success("You're on the list. Welcome to the signal.");
      setEmail("");
    } catch {
      toast.error("Subscription failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // CMS-driven values, with sensible defaults so the footer never renders blank
  const contactEmail = settings?.contact_email || "contact@wisewit.ai";
  const footerProducts = Array.isArray(settings?.footer_products) && settings.footer_products.length > 0
    ? settings.footer_products
    : [
        { name: "hr.wisewit.ai", url: "https://hr.wisewit.ai" },
        { name: "cortex.wisewit.ai", url: "https://cortex.wisewit.ai" },
      ];
  const policyDocs = Array.isArray(settings?.policy_documents)
    ? settings.policy_documents.filter((d) => d && d.url && d.name)
    : [];

  return (
    <footer data-testid="footer" className="border-t border-[#2A2A2A] mt-32 bg-[#050505]">
      <div className="px-6 md:px-10 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img src="/wisewit-emblem.png" alt="Wisewit" className="w-14 h-14 object-contain" style={{ display: 'block' }} />
            <div className="font-display text-3xl md:text-4xl tracking-tighter">
              wisewit<span className="text-[#FF4500]">.</span>ai
            </div>
          </div>
          <p className="text-[#8E8E93] mt-4 max-w-md text-[15px] leading-relaxed">
            We build the systems that run plants, lines, and operations. Computer vision,
            LLM copilots, voice interfaces, dashboards, automation — engineered to ship.
          </p>

          <form onSubmit={subscribe} className="mt-8 max-w-md" data-testid="newsletter-form">
            <label className="ww-label">Signal feed / monthly</label>
            <div className="flex items-center gap-3 border-b border-[#2A2A2A] focus-within:border-[#FF4500] transition-colors">
              <input
                type="email"
                required
                data-testid="newsletter-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@operation.com"
                className="ww-input border-0 focus:border-0"
                style={{ borderBottom: 0 }}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="btn-ghost"
              >
                {loading ? "..." : "Subscribe →"}
              </button>
            </div>
          </form>
        </div>

        <div className="md:col-span-2">
          <div className="text-overline mb-4">Navigate</div>
          <ul className="space-y-2 font-body text-sm">
            <li><Link className="text-[#8E8E93] hover:text-white" to="/solutions">Solutions</Link></li>
            <li><Link className="text-[#8E8E93] hover:text-white" to="/reels">Reels</Link></li>
            <li><Link className="text-[#8E8E93] hover:text-white" to="/systems">Systems</Link></li>
            <li><Link className="text-[#8E8E93] hover:text-white" to="/playbooks">Playbooks</Link></li>
            <li><Link className="text-[#8E8E93] hover:text-white" to="/knowledge">Knowledge</Link></li>
            <li><Link className="text-[#8E8E93] hover:text-white" to="/careers">Careers</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-overline mb-4">Products</div>
          <ul className="space-y-2 font-body text-sm" data-testid="footer-products">
            {footerProducts.map((p) => (
              <li key={p.url}>
                <a
                  className="text-[#8E8E93] hover:text-white"
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`footer-product-${(p.name || '').replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                >
                  {p.name}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="text-overline mb-4">Contact</div>
          <a href={`mailto:${contactEmail}`} className="block text-white font-display text-xl mb-3" data-testid="footer-contact-email">
            {contactEmail}
          </a>
          <a
            href="https://www.linkedin.com/company/wisewit-solutions/"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-linkedin"
            className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93] hover:text-white transition-colors"
          >
            <LinkedinLogo size={14} weight="fill" /> Follow on LinkedIn
          </a>
        </div>
      </div>

      <div className="ww-rule" />

      {policyDocs.length > 0 && (
        <div className="px-6 md:px-10 py-6 border-b border-[#2A2A2A]" data-testid="footer-policies">
          <div className="text-overline mb-3">Legal</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {policyDocs.map((d) => (
              <Link
                key={d.name}
                to={`/legal/${policySlug(d.name)}`}
                data-testid={`footer-policy-${(d.name || '').replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                className="font-body text-sm text-[#8E8E93] hover:text-white transition-colors"
              >
                {d.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="px-6 md:px-10 py-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-[#555555]">
        <div>© {new Date().getFullYear()} Wisewit Solutions Private Limited — all systems engaged</div>
        <div>Deployed on OCI / Cloudflare</div>
      </div>
    </footer>
  );
}
